import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import MultipleSelector from "@/components/ui/multiple-selector"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select"
import { GenericListManager } from "@/components/ui/generic-list-manager"
import { CurrencyInput } from "@/components/ui/currency-input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChevronDown, Plane, MapPin, Users,  Calendar as CalendarIconLucide, Files, ArrowLeft, Car, Ship, Hotel, Utensils, Package } from "lucide-react"
import { Calendar } from "@/components/ui/calendar"
import { countries, ecuadorProvinces } from "../utils/countrys-provinces"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { useAuth } from "@/context/AuthContext"
import { createTravelRequest, type TravelRequestData } from "@/lib/collections/requestsTravels/user/addRequestsTravel"
import { fetchUsersWithProfiles } from "@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile"
import { cn } from "@/lib/utils"
import { fetchCompanies } from "@/lib/collections/organizations/getOrganizationsFetch"

type Collaborator = {
    label: string;
    value: string;
};

const AddRequestTravel = () => {
    const { user } = useAuth()
    const navigate = useNavigate();
    const { id: userId } = useParams({ strict: false });

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [openStartDate, setOpenStartDate] = useState(false);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [startTime, setStartTime] = useState("10:30");
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [endTime, setEndTime] = useState("10:30");
    const [openEndDate, setOpenEndDate] = useState(false);
    const [isInternational, setIsInternational] = useState(false);
    const [address, setAddress] = useState("");
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [availableCollaborators, setAvailableCollaborators] = useState<Collaborator[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedProvinces, setSelectedProvinces] = useState<{ label: string; value: string }[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<{ label: string; value: string }[]>([]);
    const [transportationType, setTransportationType] = useState("");
    const [isGroup, setIsGroup] = useState(false);

    const [companies, setCompanies] = useState<{ label: string; value: string }[]>([]);
    const [availableCompanies, setAvailableCompanies] = useState<{ label: string; value: string }[]>([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [companiesLoadError, setCompaniesLoadError] = useState<string | null>(null);

    const [clients, setClients] = useState<string[]>([]);
    const [costTransporte, setCostTransporte] = useState("");
    const [costHospedaje, setCostHospedaje] = useState("");
    const [costAlimentacion, setCostAlimentacion] = useState("");
    const [costVarios, setCostVarios] = useState("");

    const [usersLoadError, setUsersLoadError] = useState<string | null>(null);
    const [dateError, setDateError] = useState<string>("");

    const totalCost = (parseFloat(costTransporte) || 0) + (parseFloat(costHospedaje) || 0) +
        (parseFloat(costAlimentacion) || 0) + (parseFloat(costVarios) || 0);


    useEffect(() => {
        if (isGroup) {
            setCollaborators([]);
        }
    }, [isGroup]);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoadingUsers(true);
                setUsersLoadError(null);

                const usersWithProfiles = await fetchUsersWithProfiles();

                // Filtrar usuarios activos y excluir al usuario actual
                const collaboratorOptions: Collaborator[] = usersWithProfiles
                    .filter(u => {
                        if (u.id === user?.$id) return false;
                        if (u.profile && u.profile.isActive === false) return false;
                        return true;
                    })
                    .map(u => ({
                        label: u.name,
                        value: u.id
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setAvailableCollaborators(collaboratorOptions);

                if (collaboratorOptions.length === 0) {
                    setUsersLoadError("No hay colaboradores disponibles");
                    console.warn("No hay colaboradores disponibles para seleccionar");
                }

            } catch (error: any) {
                console.error(' Error loading users:', error);
                setUsersLoadError(error.message || "Error al cargar colaboradores");
            } finally {
                setLoadingUsers(false);
            }
        };

        loadUsers();
    }, [user?.$id]);




    // Función para reintentar carga de usuarios
    const retryLoadUsers = async () => {
        setLoadingUsers(true);
        setUsersLoadError(null);

        try {
            const usersWithProfiles = await fetchUsersWithProfiles(true); // Force refresh

            const collaboratorOptions: Collaborator[] = usersWithProfiles
                .filter(u => {
                    if (u.id === user?.$id) return false;
                    if (u.profile && u.profile.isActive === false) return false;
                    return true;
                })
                .map(u => ({
                    label: u.name,
                    value: u.id
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setAvailableCollaborators(collaboratorOptions);

            if (collaboratorOptions.length === 0) {
                setUsersLoadError("No hay colaboradores disponibles");
                console.warn("No hay colaboradores disponibles después de reintentar");
            }
        } catch (error: any) {
            console.error('Error al reintentar carga de usuarios:', error);
            setUsersLoadError(error.message || "Error al cargar colaboradores");
        } finally {
            setLoadingUsers(false);
        }
    };

    // useEffect load empresas :p
    const loadCompanies = async (_forceRefresh = false) => {
        try {
            setLoadingCompanies(true);
            setCompaniesLoadError(null);
            const data = await fetchCompanies();

            const options = data
                .map(company => ({
                    label: company.name || company.commercialName || "Sin nombre",
                    value: company.$id,
                }))
                .filter(c => c.label !== "Sin nombre")
                .sort((a, b) => a.label.localeCompare(b.label));

            setAvailableCompanies(options);
        } catch (error: any) {
            console.error("Error cargando empresas:", error);
            setCompaniesLoadError(error.message || "Error al cargar empresas");
        } finally {
            setLoadingCompanies(false);
        }
    };

    // useEffect usa la función
    useEffect(() => {
        loadCompanies();
    }, []);


    // Validar fechas de inicio y fin 
    useEffect(() => {
        if (startDate && endDate && startTime && endTime) {
            const combinedStart = new Date(startDate);
            const [startHour, startMinute] = startTime.split(':');
            combinedStart.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

            const combinedEnd = new Date(endDate);
            const [endHour, endMinute] = endTime.split(':');
            combinedEnd.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

            if (combinedEnd <= combinedStart) {
                setDateError("La fecha final debe ser posterior a la fecha inicial");
            } else {
                setDateError("");
            }
        }
    }, [startDate, endDate, startTime, endTime]);

    const handleSaveEvent = async () => {
        if (isSubmitting) return;

        let loadingToast: any = null;

        try {
            setIsSubmitting(true);

            // Validaciones
            if (!title.trim()) {
                toast.warning("El título es obligatorio");
                return;
            }

            if (!description.trim()) {
                toast.warning("El motivo detallado es obligatorio");
                return;
            }


            if (!startDate) {
                toast.warning("La fecha de inicio es obligatoria");
                return;
            }

            if (!endDate) {
                toast.warning("La fecha final es obligatoria");
                return;
            }

            if (dateError) {
                toast.warning(dateError);
                return;
            }

            if (!isGroup && !user?.$id) {
                toast.warning("Error: Usuario no identificado");
                return;
            }

            if (isInternational) {
                if (selectedCountries.length === 0) {
                    toast.warning("Debes seleccionar al menos un país de destino");
                    return;
                }
            } else {
                if (selectedProvinces.length === 0) {
                    toast.warning("Debes seleccionar al menos una provincia de destino");
                    return;
                }
            }

            if (!transportationType) {
                toast.warning("Debes seleccionar un tipo de transporte");
                return;
            }


            loadingToast = toast.loading("Guardando solicitud de viaje...");

            // Combinar fecha + hora para crear timestamp completo
            const combinedStartDate = new Date(startDate);
            const [startHour, startMinute] = startTime.split(':');
            combinedStartDate.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

            const combinedEndDate = new Date(endDate);
            const [endHour, endMinute] = endTime.split(':');
            combinedEndDate.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

            // Preparar usersId: solo el usuario actual + colaboradores si es grupal
            const usersIdArray = [user!.$id];
            if (isGroup && collaborators.length > 0) {
                usersIdArray.push(...collaborators.map(c => c.value));
            }

            // Preparar datos para enviar
            const travelRequestData: TravelRequestData = {
                userId: user!.$id,
                usersId: usersIdArray,
                title: title.trim(),
                tripPurpose: description.trim() || "Sin descripción",
                startDate: combinedStartDate.toISOString(),
                endDate: combinedEndDate.toISOString(),
                status: "pending",
                address: address.trim() || undefined,
                isInternational: isInternational,
                transportationType: transportationType || undefined,
                estimatedCost: [
                    parseFloat(costTransporte) || 0,
                    parseFloat(costHospedaje) || 0,
                    parseFloat(costAlimentacion) || 0,
                    parseFloat(costVarios) || 0,
                ],
                companies: companies.map(company => company.value),
                clients: clients,
                statusByRrhh: "pending",
                idApprovedByRrhh: null,
                isGroup: isGroup,
                provinces: isInternational ? [] : selectedProvinces.map(p => p.value),
                countries: isInternational ? selectedCountries.map(c => c.value) : [],
            };

            // Preparar destino legible para el webhook
            const destination = isInternational
                ? selectedCountries.map(country => country.label).join(', ')
                : selectedProvinces.map(province => province.label).join(', ');

            await createTravelRequest(
                travelRequestData,
                user!.name,
                user!.email,
                destination || "No especificado"
            );

            toast.dismiss(loadingToast);
            toast.success("Solicitud de viaje creada exitosamente");

            // Resetear formulario
            setTitle("");
            setDescription("");
            setIsInternational(false);
            setIsGroup(false);
            setStartDate(undefined);
            setEndDate(undefined);
            setStartTime("10:30");
            setEndTime("10:30");
            setCollaborators([]);
            setCostTransporte("");
            setCostHospedaje("");
            setCostAlimentacion("");
            setCostVarios("");
            setAddress("");
            setSelectedProvinces([]);
            setSelectedCountries([]);
            setTransportationType("");
            setCompanies([]);
            setClients([]);

        } catch (error) {
            if (loadingToast) toast.dismiss(loadingToast);
            toast.error("Error al crear la solicitud de viaje");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (

        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Permisos</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests/$id/travels" params={{ id: userId! }}>
                                Registros
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />

                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Permisos</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            <Plane className="w-5 h-5" />
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 font-outfit">
                <div className="max-w-8xl mx-auto">
                    <Card className="shadow-xl border-0 p-0">
                        <CardHeader className="relative text-white rounded-t-lg bg-blue-500 h-20">
                            {/* Fondo azul absoluto */}
                            <div className="absolute top-0 rounded-t-4xl left-0 w-full h-full bg-blue-500"></div>

                            {/* Contenido encima del fondo */}
                            <div className="relative z-10 flex items-center gap-4 py-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Plane className="w-6 h-6" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <CardTitle className="text-2xl">Solicitud de Viaje</CardTitle>
                                    <CardDescription className="text-blue-100">
                                        Completa los datos de tu viaje..
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>

                        <CardContent className="p-6 sm:p-8">
                            <div className="space-y-8">
                                {/* Información Básica */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            1
                                        </div>
                                        Información Básica
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="title" className="text-sm font-medium">Título *</Label>
                                            <Input
                                                id="title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                placeholder="Ej: Reunión con cliente en Guayaquil"
                                                className="mt-1.5" />
                                        </div>

                                        <div>
                                            <Label htmlFor="description" className="text-sm font-medium">Motivo detallado</Label>
                                            <Textarea
                                                id="description"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder="Describe el motivo del viaje, objetivos y actividades planeadas..."
                                                className="mt-1.5 min-h-25" />
                                        </div>
                                    </div>
                                </section>

                                {/* Fechas y Horarios */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            2
                                        </div>
                                        Fechas y Horarios
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Fecha y hora inicial */}
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                                    <CalendarIconLucide className="w-4 h-4" />
                                                    Fecha inicial *
                                                </Label>
                                                <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                "w-full justify-between font-normal",
                                                                dateError && "border-red-500 focus-visible:ring-red-500"
                                                            )}
                                                        >
                                                            {startDate ? startDate.toLocaleDateString('es-ES', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }) : "Seleccionar fecha"}
                                                            <ChevronDown className="w-4 h-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar
                                                            mode="single"
                                                            selected={startDate}
                                                            captionLayout="dropdown"
                                                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                                            onSelect={(date) => {
                                                                setStartDate(date)
                                                                setOpenStartDate(false)
                                                            }} />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div>
                                                <Label htmlFor="start-time" className="text-sm font-medium">Hora inicial</Label>
                                                <Input
                                                    type="time"
                                                    id="start-time"
                                                    value={startTime}
                                                    onChange={(e) => setStartTime(e.target.value)}
                                                    className={cn(
                                                        "mt-1.5 [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
                                                        dateError && "border-red-500 focus-visible:ring-red-500"
                                                    )} />
                                            </div>
                                        </div>

                                        {/* Fecha y hora final */}
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                                    <CalendarIconLucide className="w-4 h-4" />
                                                    Fecha final
                                                </Label>
                                                <Popover open={openEndDate} onOpenChange={setOpenEndDate}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                "w-full justify-between font-normal",
                                                                dateError && "border-red-500 focus-visible:ring-red-500"
                                                            )}
                                                        >
                                                            {endDate ? endDate.toLocaleDateString('es-ES', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }) : "Seleccionar fecha"}
                                                            <ChevronDown className="w-4 h-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar
                                                            mode="single"
                                                            selected={endDate}
                                                            captionLayout="dropdown"
                                                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                                            onSelect={(date) => {
                                                                setEndDate(date)
                                                                setOpenEndDate(false)
                                                            }} />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div>
                                                <Label htmlFor="end-time" className="text-sm font-medium">Hora finalización</Label>
                                                <Input
                                                    type="time"
                                                    id="end-time"
                                                    value={endTime}
                                                    onChange={(e) => setEndTime(e.target.value)}
                                                    className={cn(
                                                        "mt-1.5 [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
                                                        dateError && "border-red-500 focus-visible:ring-red-500"
                                                    )} />
                                            </div>
                                        </div>
                                        {dateError && (
                                            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2 mt-2">
                                                <span className="text-red-500">⚠</span>
                                                {dateError}
                                            </p>
                                        )}
                                    </div>
                                </section>

                                {/* Destino */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            3
                                        </div>
                                        Destino y Transporte
                                    </h3>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <Switch
                                                id="isInternational"
                                                checked={isInternational}
                                                onCheckedChange={setIsInternational} />
                                            <Label htmlFor="isInternational" className="cursor-pointer flex items-center gap-2">
                                                <MapPin className="w-4 h-4" />
                                                ¿Es un viaje internacional?
                                            </Label>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-sm font-medium">
                                                    {isInternational ? "Selecciona un país o varios" : "Selecciona una provincia o varias"}
                                                </Label>
                                                {isInternational ? (
                                                    <div className="mt-1.5">
                                                        <MultipleSelector
                                                            key="countries"
                                                            value={selectedCountries}
                                                            onChange={setSelectedCountries}
                                                            defaultOptions={countries}
                                                            placeholder="Seleccionar países..."
                                                            emptyIndicator={
                                                                <p className="text-center text-sm py-4 text-slate-500">
                                                                    No se encontraron resultados
                                                                </p>
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="mt-1.5">
                                                        <MultipleSelector
                                                            key="provinces"
                                                            value={selectedProvinces}
                                                            onChange={setSelectedProvinces}
                                                            defaultOptions={ecuadorProvinces}
                                                            placeholder="Seleccionar provincias..."
                                                            emptyIndicator={
                                                                <p className="text-center text-sm py-4 text-slate-500">
                                                                    No se encontraron resultados
                                                                </p>
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="transport" className="text-sm font-medium">Tipo de transporte</Label>
                                                <Select value={transportationType} onValueChange={setTransportationType}>
                                                    <SelectTrigger className="mt-1.5">
                                                        <SelectValue placeholder="Seleccionar tipo" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectGroup>
                                                            <SelectLabel>Tipo de transporte</SelectLabel>
                                                            <SelectItem value="land"><Car/>  Terrestre</SelectItem>
                                                            <SelectItem value="air"><Plane/> Aéreo</SelectItem>
                                                            <SelectItem value="sea"><Ship/>  Marítimo</SelectItem>
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="address" className="text-sm font-medium">Dirección detallada</Label>
                                            <Input
                                                id="address"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                                placeholder="Dirección completa del destino"
                                                className="mt-1.5" />
                                        </div>
                                    </div>
                                </section>

                                {/* Colaboradores */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            4
                                        </div>
                                        Colaboradores
                                    </h3>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <Switch
                                                id="isGroup"
                                                checked={isGroup}
                                                onCheckedChange={setIsGroup} />
                                            <Label htmlFor="isGroup" className="cursor-pointer flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                ¿Viaje grupal con más colaboradores?
                                            </Label>
                                        </div>

                                        {isGroup && (
                                            <div className="space-y-3">
                                                {/* Mensaje de error con botón de reintento */}
                                                {usersLoadError && (
                                                    <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                                                No se pudieron cargar los colaboradores
                                                            </p>
                                                            <Button
                                                                onClick={retryLoadUsers}
                                                                disabled={loadingUsers}
                                                                size="sm"
                                                                variant="outline"
                                                                className="mt-2 h-8 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                                            >
                                                                {loadingUsers ? "Cargando..." : " Reintentar"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                <Label className="text-sm font-medium">
                                                    Selecciona colaboradores
                                                </Label>

                                                {collaborators.length > 0 && (
                                                    <p className="text-sm text-blue-600 dark:text-blue-400">
                                                        Seleccionados: <span className="text-slate-600 dark:text-slate-400">
                                                            {collaborators.map((c) => c.label).join(', ')}
                                                        </span>
                                                    </p>
                                                )}

                                                <MultipleSelector
                                                    value={collaborators}
                                                    onChange={setCollaborators}
                                                    defaultOptions={availableCollaborators}
                                                    placeholder={loadingUsers ? "Cargando colaboradores..." : "Buscar colaboradores..."}
                                                    disabled={loadingUsers || !!usersLoadError}
                                                    emptyIndicator={
                                                        <p className="text-center text-sm py-4 text-slate-500">
                                                            {loadingUsers
                                                                ? "Cargando colaboradores..."
                                                                : usersLoadError
                                                                    ? "Error al cargar. Usa el botón de reintentar arriba."
                                                                    : "No se encontraron colaboradores"}
                                                        </p>
                                                    }
                                                />

                                                {/* Contador de colaboradores disponibles */}
                                                {!loadingUsers && !usersLoadError && availableCollaborators.length > 0 && (
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {availableCollaborators.length} colaborador{availableCollaborators.length !== 1 ? 'es' : ''} disponible{availableCollaborators.length !== 1 ? 's' : ''}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/* Información Financiera */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            5
                                        </div>
                                        Información Financiera
                                    </h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-sm font-medium"><Car/> Transporte (USD)</Label>
                                            <CurrencyInput value={costTransporte} onChange={setCostTransporte} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Hotel/> Hospedaje (USD)</Label>
                                            <CurrencyInput value={costHospedaje} onChange={setCostHospedaje} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Utensils /> Alimentación (USD)</Label>
                                            <CurrencyInput value={costAlimentacion} onChange={setCostAlimentacion} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                        <div>
                                            <Label className="text-sm font-medium"><Package/>  Varios (USD)</Label>
                                            <CurrencyInput value={costVarios} onChange={setCostVarios} placeholder="0.00" showExample={false} className="mt-1.5" />
                                        </div>
                                    </div>

                                    {totalCost > 0 && (
                                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                                                Total estimado: ${totalCost.toFixed(2)} USD
                                            </p>
                                        </div>
                                    )}
                                </section>
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            6
                                        </div>
                                        Entidades y clientes
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <Label className="text-sm font-medium">Empresas o instituciones a visitar</Label>

                                            {companiesLoadError && (
                                                <div className="flex items-start gap-3 p-3 mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                                            No se pudieron cargar las empresas
                                                        </p>
                                                        <Button
                                                            onClick={() => loadCompanies()}
                                                            disabled={loadingCompanies}
                                                            size="sm"
                                                            variant="outline"
                                                            className="mt-2 h-8 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                                        >
                                                            {loadingCompanies ? "Cargando..." : "🔄 Reintentar"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-1.5">
                                                <MultipleSelector
                                                    value={companies}
                                                    onChange={setCompanies}
                                                    defaultOptions={availableCompanies}
                                                    placeholder={loadingCompanies ? "Cargando empresas..." : "Buscar empresas..."}
                                                    disabled={loadingCompanies || !!companiesLoadError}
                                                    badgeClassName="max-w-[180px] truncate"
                                                    emptyIndicator={
                                                        <p className="text-center text-sm py-4 text-slate-500">
                                                            {loadingCompanies
                                                                ? "Cargando..."
                                                                : companiesLoadError
                                                                    ? "Error al cargar. Usa el botón de reintentar."
                                                                    : "No se encontraron empresas"}
                                                        </p>
                                                    }
                                                />
                                            </div>

                                            {!loadingCompanies && !companiesLoadError && availableCompanies.length > 0 && (
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {availableCompanies.length} empresa{availableCompanies.length !== 1 ? 's' : ''} disponible{availableCompanies.length !== 1 ? 's' : ''}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <GenericListManager
                                                items={clients}
                                                onChange={setClients}
                                                label="Clientes"
                                                placeholder="Nombre del cliente..."
                                                singularLabel="cliente"
                                                pluralLabel="clientes"
                                            />
                                        </div>
                                    </div>
                                </section>
                            </div>


                            {/* Botones de Acción */}
                            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t">
                                <Button
                                    onClick={handleSaveEvent}
                                    disabled={isSubmitting}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 cursor-pointer text-white font-medium"
                                >
                                    {isSubmitting ? "Guardando..." : "Guardar Solicitud"}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="flex-1 sm:flex-none sm:px-8 cursor-pointer"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (userId) {
                                            navigate({ to: '/requests/$id/travels', params: { id: userId } });
                                        }
                                    }}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Atrás
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div ></>
    );
};

export default AddRequestTravel;